import {
	AssistantRuntimeProvider,
	useAssistantApi,
	useAssistantEvent,
	useAssistantState,
} from '@assistant-ui/react'
import { useDataStreamRuntime } from '@assistant-ui/react-data-stream'
import { trackEvent } from 'fathom-client'
import { useEffect, useMemo, useRef } from 'react'
import { AdProvider, SideBarAd } from '#app/components/assistant-ui/ads.js'
import { NewChatButton } from '#app/components/assistant-ui/new-chat-button.js'
import { OpenInAssistantButton } from '#app/components/assistant-ui/open-in-assistant-button.js'
import {
	DEFAULT_REGION,
	RegionContext,
} from '#app/components/assistant-ui/region-picker.js'
import { Thread } from '#app/components/assistant-ui/thread.js'
import { FIRST_ANSWER_EVENT } from '#app/components/install-prompt.js'
import { TooltipProvider } from '#app/components/ui/tooltip.js'
import {
	ANSWER_ERROR_DATA_TYPE,
	ANSWER_ERROR_KINDS,
	type AnswerErrorPayload,
} from '#app/utils/answer-error.ts'
import { type ChatVariant } from '#app/utils/chat-query.js'
import { type ChatSurface } from '#app/utils/chat-surfaces.ts'
import { extractLastDataPart } from '#app/utils/data-parts.js'
import { trackAnswerError, trackUIEvent } from '#app/utils/plausible.client.js'
import { RUTOR_CAPABILITY } from '#app/utils/rutor.ts'
import { isTestingMode } from '#app/utils/test-mode.client.js'
import { takeInputMethod } from '#app/utils/voice-input-origin.ts'

interface ChatPageProps {
	conversationId: string
	query: string | null
	variant: ChatVariant
	onNewChat?: () => void
	/** TEST-UI (P2f): vald region (REGIONS-kod). Utelämnad eller 'all' =
	 * ordinarie beteende. Skickas per förfrågan, så ett byte gäller från nästa
	 * fråga utan att tråden startas om. */
	region?: string
	/** Sidans regionval (variant A): utan den renderas inga regionkontroller
	 * (embed), men `region` skickas fortfarande i anropet. */
	onSelectRegion?: (region: string) => void
}

/**
 * Shared chat UI rendered by both `/` and `/embed`. Owns the assistant-ui
 * runtime, query auto-submit, and analytics tracking. The parent route owns
 * `conversationId` state and uses `<ChatPage key={conversationId} ... />`
 * to remount this subtree on "Ny konversation" — remounting aborts any
 * in-flight request and creates a brand new runtime.
 */
export function ChatPage({
	conversationId,
	query,
	variant,
	onNewChat,
	region,
	onSelectRegion,
}: ChatPageProps) {
	// The testing flag is read FRESH at fetch-time via a headers function, not
	// from React state. useDataStreamRuntime awaits headers() inside its run() on
	// every send (never during SSR/render), so isTestingMode() - a client-only
	// read from test-mode.client.ts - is safe here and always current.
	//
	// This is deliberate: an earlier version kept `testing` in React state fed
	// into the request body, but the `?q=` auto-submit (AutoSubmitQuery) fires as
	// a child effect before this component's deferred effect could set the flag,
	// so the handoff query - the single most important one to flag (eval runs,
	// local-dev auto-on) - went out unflagged. Reading at fetch-time removes the
	// race entirely. Load-bearing: syncTestingFlagFromUrl() runs pre-hydration in
	// entry.client.tsx, so localStorage is set before any fetch reads it.
	const runtime = useDataStreamRuntime({
		api: '/resources/chat',
		// Read fresh on every send: useLocalRuntime swaps in the newly constructed
		// adapter whenever these options change, so a region picked mid-thread
		// applies from the next question — no remount, no lost conversation.
		// 'all' is left out entirely so a region-less request stays byte-identical.
		body: {
			conversationId,
			// Bara för AI-svarsloggen (chat-surfaces.ts); samma namn som
			// Plausible-propen `location` nedan.
			surface: (variant === 'embed'
				? 'assistent_embed'
				: 'assistent_start_page') satisfies ChatSurface,
			...(region && region !== 'all' ? { region } : {}),
			// Den här klienten kan rita rutor (lathundar, koder, kalkylatorer) och
			// tolka [[ruta:…]]-markörer; utan flaggan skickar servern inga.
			capabilities: [RUTOR_CAPABILITY],
		},
		headers: async () => {
			const headers: Record<string, string> = {}
			if (isTestingMode()) headers['x-im-testing'] = '1'
			return headers
		},
	})

	// Ett regionval för hela sidan: förstasidans mening, composerns pill och
	// Snabbsök-kortet läser alla samma context (region-picker.tsx).
	const regionCtx = useMemo(
		() => ({ region: region ?? DEFAULT_REGION, onSelectRegion }),
		[region, onSelectRegion],
	)

	return (
		<AssistantRuntimeProvider runtime={runtime}>
			<RegionContext.Provider value={regionCtx}>
				<AdProvider>
					<TooltipProvider>
						<TrackQuestionAsked variant={variant} />
						<TrackAnswerError variant={variant} />
						<AutoSubmitQuery query={query} />
						{variant === 'embed' ? (
							<OpenInAssistantButton />
						) : onNewChat ? (
							<NewChatButton onNewChat={onNewChat} />
						) : null}
						<Thread variant={variant} />
						{variant !== 'embed' ? <SideBarAd /> : null}
					</TooltipProvider>
				</AdProvider>
			</RegionContext.Provider>
		</AssistantRuntimeProvider>
	)
}

/**
 * `input_method` (voice | voice_edited | text) på question_asked — bara när
 * röstinmatningen är på och utanför embed. Med flaggan av är eventet
 * byte-identiskt med tidigare, så befintliga mål och serier påverkas inte.
 * Läser senaste användarmeddelandet imperativt (som thread.tsx), och bara
 * etiketten lämnar klienten — aldrig texten.
 */
function inputMethodProps(
	api: ReturnType<typeof useAssistantApi>,
	variant: ChatVariant,
): Record<string, string> | undefined {
	if (variant === 'embed' || window.ENV?.ENABLE_VOICE_INPUT !== '1') {
		return undefined
	}
	const messages = api.thread().getState().messages
	const lastUser = [...messages].reverse().find(m => m.role === 'user')
	const text = (lastUser?.content ?? [])
		.map((c: { type: string; text?: string }) =>
			c.type === 'text' ? c.text ?? '' : '',
		)
		.join(' ')
	return { input_method: takeInputMethod(text) }
}

// Exporterad för enhetstest; används bara i denna fil.
export function TrackQuestionAsked({ variant }: { variant: ChatVariant }) {
	// Via ref, inte i effektens beroenden: byter api identitet får effekten
	// inte köras om — då skulle question_asked räknas två gånger.
	const api = useAssistantApi()
	const apiRef = useRef(api)
	apiRef.current = api
	const isRunning = useAssistantState(({ thread }) => thread.isRunning)
	const wasRunningRef = useRef(false)
	const firstAnswerDispatchedRef = useRef(false)

	useEffect(() => {
		if (isRunning) {
			trackEvent(
				variant === 'embed' ? 'Question Asked v2 embed' : 'Question Asked v2',
			)
			trackUIEvent({
				eventName: 'form_submit',
				label: 'question_asked_v2',
				location:
					variant === 'embed' ? 'assistent_embed' : 'assistent_start_page',
				extraProps: inputMethodProps(apiRef.current, variant),
			})
			wasRunningRef.current = true
			return
		}

		// isRunning flipped true → false: the first answer just finished
		// streaming. Notify the install prompt so it can show after a short
		// reading delay. Only fire once per page session — subsequent answers
		// are not engagement signals the install prompt cares about.
		if (
			wasRunningRef.current &&
			!firstAnswerDispatchedRef.current &&
			variant !== 'embed'
		) {
			firstAnswerDispatchedRef.current = true
			window.dispatchEvent(new CustomEvent(FIRST_ANSWER_EVENT))
		}
	}, [isRunning, variant])

	return null
}

/**
 * Ett `answer_error`-event per körning som slutade i fel eller ofullständigt
 * svar. Servern skickar dataparten `answerError` före felramen (chat.ts);
 * den ligger i sista assistentmeddelandets metadata.unstable_data när
 * thread.run-end fyras (en gång per körning, efter att meddelandet är
 * committat). Läses imperativt som i thread.tsx så vi inte prenumererar på
 * varje streamad token. Bara kinds i allowlistan släpps igenom — en trasig
 * server får aldrig skicka fritext till Plausible.
 */
// Exporterad för enhetstest; används bara i denna fil.
export function TrackAnswerError({ variant }: { variant: ChatVariant }) {
	const api = useAssistantApi()
	// "Exakt en gång" ska inte vila på att runtimen fyrar run-end exakt en
	// gång per körning: kom ihåg senast rapporterade meddelande-id.
	const reportedMessageId = useRef<string | null>(null)
	useAssistantEvent('thread.run-end', () => {
		const messages = api.thread().getState().messages
		const last = messages[messages.length - 1]
		if (!last || last.role !== 'assistant') return
		if (reportedMessageId.current === last.id) return
		const payload = extractLastDataPart<AnswerErrorPayload>(
			last.metadata as Record<string, unknown> | undefined,
			ANSWER_ERROR_DATA_TYPE,
		)
		if (
			!payload ||
			!(ANSWER_ERROR_KINDS as readonly string[]).includes(payload.kind)
		)
			return
		// Samma allowlist-tanke som för kind: okänd motor släpps inte igenom.
		// Bara egen motor svarar sedan IM-60 ('docsbot' i typen är historiskt).
		if (payload.engine !== 'selfbuilt') return
		reportedMessageId.current = last.id
		trackAnswerError({
			kind: payload.kind,
			engine: payload.engine,
			location:
				variant === 'embed' ? 'assistent_embed' : 'assistent_start_page',
		})
	})
	return null
}

/**
 * Component that automatically submits a query when provided.
 * Must be used inside AssistantRuntimeProvider.
 */
// Exported for unit testing the ?q= cleanup guard; not used outside this file.
export function AutoSubmitQuery({ query }: { query: string | null }) {
	const api = useAssistantApi()
	const hasSubmitted = useRef(false)

	useEffect(() => {
		if (query && !hasSubmitted.current) {
			hasSubmitted.current = true

			// Remove q param from URL without page reload. history.replaceState
			// throws SecurityError in an opaque-origin sandboxed iframe; the URL
			// cleanup is cosmetic, so skip it on failure and still submit below.
			try {
				const url = new URL(window.location.href)
				url.searchParams.delete('q')
				window.history.replaceState({}, '', url.toString())
			} catch {
				// opaque-origin sandbox - leave the URL as-is
			}

			const composer = api.composer()
			composer.setText(query)
			composer.send()
		}
	}, [query, api])

	return null
}
